import { Component, OnDestroy, OnInit, TemplateRef, ViewChild, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Subscription, catchError, debounceTime, distinctUntilChanged, finalize, forkJoin, of, take } from 'rxjs';
import { AuthService } from 'src/app/pages/auth/services/auth.service';
import { ScheduleSubmissionsService } from 'src/app/services/api/schedule-submissions/schedule-submissions.service';
import { StudyPeriodService } from 'src/app/services/api/study-period/study-period.service';
import { UserManagementService } from 'src/app/services/api/user-management/user-management.service';
import { ScheduleSubmissionsModel } from 'src/app/services/models/schedule/schedule-submissions.model';
import { AcademicTermYearModel } from 'src/app/services/models/study-period/study-period.model';
import { UserManagementOption } from 'src/app/services/models/user-management/user-management.model';
import { RoleID } from 'src/utils/enum/role.enum';
import {
  getScheduleSubmissionListStatusDisplay,
  ScheduleSubmissionListDisplayState,
  ScheduleSubmissionListStatusDisplay,
} from '../schedule-submission-status-display';
import {
  createDatatableSortParams,
  DatatableSortChangeEvent,
  DatatableSortFieldMap,
  DatatableSortParams,
} from 'src/shared/datatable/interfaces/datatable.interface';
import { SharedModule } from 'src/shared/shared.module';

interface ScheduleSubmissionRow {
  id: number;
  sequence?: number | string | null;
  academicTerm: string;
  year: string;
  teacherName: string;
  departmentName: string;
  statusCode: string;
  statusLabel: string;
  updatedAt: string;
}

interface ScheduleSubmissionsDataSource {
  data: ScheduleSubmissionRow[];
  limit: number;
  offset: number;
  page: number;
  total: number;
  total_page: number;
}

@Component({
  selector: 'app-schedule-submissions',
  standalone: true,
  imports: [
    SharedModule,
    ReactiveFormsModule,
    TranslateModule,
  ],
  templateUrl: './schedule-submissions.component.html',
  styleUrl: './schedule-submissions.component.scss',
})
export class ScheduleSubmissionsComponent implements OnInit, OnDestroy {
  @ViewChild('rowIndexTemplate', { static: true })
  rowIndexTemplate!: TemplateRef<any>;
  @ViewChild('bulkSelectHeaderTemplate', { static: true })
  bulkSelectHeaderTemplate!: TemplateRef<any>;
  @ViewChild('bulkSelectCellTemplate', { static: true })
  bulkSelectCellTemplate!: TemplateRef<any>;
  @ViewChild('actionTemplate', { static: true })
  actionTemplate!: TemplateRef<any>;
  @ViewChild('statusTemplate', { static: true })
  statusTemplate!: TemplateRef<any>;

  form!: FormGroup;
  columns: any[] = [];
  datasource = signal<ScheduleSubmissionsDataSource>(this.createDataSource([]));
  tableLoading = signal(true);
  bulkConfirmLoading = signal(false);
  bulkConfirmModalOpen = signal(false);
  bulkConfirmResultMessage = signal<string | null>(null);
  selectedSubmission = signal<ScheduleSubmissionRow | null>(null);
  selectedSubmissionIds = signal<Set<number>>(new Set<number>());
  pageSizeOptions = [10, 20, 50];
  academicTermOptions: string[] = [];
  yearOptions: string[] = [];
  departmentOptions: UserManagementOption[] = [];

  private currentPage = 1;
  private currentLimit = 10;
  private currentSortParams: DatatableSortParams = { sort: null, direction: null };
  private readonly sortFieldMap: DatatableSortFieldMap = {
    index: 'sequence',
    academicTerm: 'academic_term_name',
    year: 'academic_term_year',
    teacherName: 'full_name',
      departmentName: 'department',
      updatedAt: 'updated_at',
  };
  private filterSubscription?: Subscription;
  private filterOptionsSubscription?: Subscription;
  private loadSubscription?: Subscription;
  private route = inject(ActivatedRoute);
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private authService = inject(AuthService);
  private scheduleSubmissionsService = inject(ScheduleSubmissionsService);
  private studyPeriodService = inject(StudyPeriodService);
  private userManagementService = inject(UserManagementService);
  private academicTerms: AcademicTermYearModel[] = [];

  ngOnInit(): void {
    this.initForm();
    this.initColumns();
    this.listenFilters();
    this.loadFilterOptions();
  }

  ngOnDestroy(): void {
    this.filterSubscription?.unsubscribe();
    this.filterOptionsSubscription?.unsubscribe();
    this.loadSubscription?.unsubscribe();
  }

  onSearch(): void {
    this.currentPage = 1;
    this.loadSubmissions(1);
  }

  onViewSubmission(row: ScheduleSubmissionRow): void {
    this.selectedSubmission.set(row);
    void this.router.navigate([
      this.isApprovalReviewMode()
        ? '/schedule-submissions-management/schedule-submissions-review-details'
        : '/schedule-submissions-management/schedule-submissions-details',
      row.id,
    ]);
  }

  onToggleBulkSelection(row: ScheduleSubmissionRow, checked: boolean): void {
    if (!this.canSelectForBulkEducationHeadConfirm(row)) {
      return;
    }

    const nextSelectedIds = new Set(this.selectedSubmissionIds());
    if (checked) {
      nextSelectedIds.add(row.id);
    } else {
      nextSelectedIds.delete(row.id);
    }

    this.selectedSubmissionIds.set(nextSelectedIds);
  }

  onToggleBulkSelectAllCurrentPage(checked: boolean): void {
    const eligibleRowIds = this.currentPageEligibleBulkRows().map((row) => row.id);
    const nextSelectedIds = new Set(this.selectedSubmissionIds());

    eligibleRowIds.forEach((id) => {
      if (checked) {
        nextSelectedIds.add(id);
      } else {
        nextSelectedIds.delete(id);
      }
    });

    this.selectedSubmissionIds.set(nextSelectedIds);
  }

  onOpenBulkEducationHeadConfirm(): void {
    if (!this.canBulkEducationHeadConfirm()) {
      return;
    }

    this.bulkConfirmResultMessage.set(null);
    this.bulkConfirmModalOpen.set(true);
  }

  onCloseBulkEducationHeadConfirm(): void {
    if (this.bulkConfirmLoading()) {
      return;
    }

    this.bulkConfirmModalOpen.set(false);
  }

  onConfirmBulkEducationHeadConfirm(): void {
    const selectedIds = this.selectedBulkEducationHeadConfirmIds();
    if (selectedIds.length === 0 || this.bulkConfirmLoading()) {
      return;
    }

    this.bulkConfirmLoading.set(true);
    this.scheduleSubmissionsService.bulkEducationHeadConfirm(selectedIds).pipe(
      finalize(() => this.bulkConfirmLoading.set(false)),
    ).subscribe({
      next: (response) => {
        const successCount = response.success_ids.length;
        const failedCount = response.failed.length;
        const failedReasons = response.failed
          .map((item) => `#${item.id}: ${item.error}`)
          .join('\n');

        this.bulkConfirmResultMessage.set(
          failedCount > 0
            ? `รับรองสำเร็จ ${successCount} รายการ ไม่สำเร็จ ${failedCount} รายการ${failedReasons ? `\n${failedReasons}` : ''}`
            : `รับรองตารางสอนสำเร็จ ${successCount} รายการ`
        );
        this.bulkConfirmModalOpen.set(false);
        this.selectedSubmissionIds.set(new Set<number>());
        this.loadSubmissions(this.currentPage);
      },
      error: (err) => {
        console.error(err);
        this.bulkConfirmResultMessage.set('ไม่สามารถรับรองตารางสอนที่เลือกได้ กรุณาลองใหม่อีกครั้ง');
      },
    });
  }

  onPageChange(page: number): void {
    if (page < 1 || page > this.datasource().total_page || page === this.currentPage) {
      return;
    }

    this.loadSubmissions(page);
  }

  onLimitChange(limit: number): void {
    if (!Number.isFinite(limit) || limit === this.currentLimit) {
      return;
    }

    this.currentLimit = limit;
    this.loadSubmissions(1);
  }

  onSortChange(event: DatatableSortChangeEvent): void {
    this.currentSortParams = createDatatableSortParams(event, this.sortFieldMap);
    this.loadSubmissions(1);
  }

  canViewManagerFilters(): boolean {
    const activeRoleId = this.authService.getActiveRoleId();

    return activeRoleId != null && [
      RoleID.ACADEMIC_OFFICER,
      RoleID.HEAD_OF_EDUCATION,
    ].includes(activeRoleId);
  }

  isApprovalReviewMode(): boolean {
    return this.route.snapshot.data['approvalReview'] === true;
  }

  canShowBulkEducationHeadConfirm(): boolean {
    return false;
  }

  isBulkSelected(row: ScheduleSubmissionRow): boolean {
    return this.selectedSubmissionIds().has(row.id);
  }

  canSelectForBulkEducationHeadConfirm(row: ScheduleSubmissionRow): boolean {
    return this.canShowBulkEducationHeadConfirm() &&
      ['teacher_confirmed', 'waiting_education_head_review'].includes(row.statusCode);
  }

  selectedBulkEducationHeadConfirmIds(): number[] {
    const selectedIds = this.selectedSubmissionIds();

    return this.datasource()
      .data
      .filter((row) => selectedIds.has(row.id) && this.canSelectForBulkEducationHeadConfirm(row))
      .map((row) => row.id);
  }

  selectedBulkEducationHeadConfirmCount(): number {
    return this.selectedBulkEducationHeadConfirmIds().length;
  }

  canBulkEducationHeadConfirm(): boolean {
    return this.selectedBulkEducationHeadConfirmCount() > 0 && !this.bulkConfirmLoading();
  }

  isAllCurrentPageBulkSelected(): boolean {
    const eligibleRows = this.currentPageEligibleBulkRows();
    return eligibleRows.length > 0 && eligibleRows.every((row) => this.selectedSubmissionIds().has(row.id));
  }

  isSomeCurrentPageBulkSelected(): boolean {
    return this.currentPageEligibleBulkRows().some((row) => this.selectedSubmissionIds().has(row.id));
  }

  pageTitle(): string {
    return this.isApprovalReviewMode()
      ? 'การจัดการตารางสอน - ตรวจสอบและรับรองตารางสอน'
      : 'การจัดการตารางสอน - ตารางสอน';
  }

  getStatusClass(statusCode: string | null | undefined): string {
    if (this.getListStatusDisplay(statusCode).code === 'special_revision_requested') {
      return 'schedule-submissions-status-badge-special-revision';
    }

    const classes: Record<ScheduleSubmissionListDisplayState, string> = {
      draft: 'schedule-submissions-status-badge-waiting-submit',
      pending: 'schedule-submissions-status-badge-pending',
      revision: 'schedule-submissions-status-badge-revision',
      waiting_review: 'schedule-submissions-status-badge-waiting-review',
      completed: 'schedule-submissions-status-badge-completed',
    };

    return classes[this.getListStatusDisplay(statusCode).displayState];
  }

  isRevisionStatus(statusCode: string | null | undefined): boolean {
    return this.getStatusClass(statusCode) === 'schedule-submissions-status-badge-revision';
  }

  isSpecialRevisionStatus(statusCode: string | null | undefined): boolean {
    return this.getStatusClass(statusCode) === 'schedule-submissions-status-badge-special-revision';
  }

  isPendingStatus(statusCode: string | null | undefined): boolean {
    return this.getStatusClass(statusCode) === 'schedule-submissions-status-badge-pending';
  }

  isCompletedStatus(statusCode: string | null | undefined): boolean {
    return this.getStatusClass(statusCode) === 'schedule-submissions-status-badge-completed';
  }

  isWaitingTeacherSubmitStatus(statusCode: string | null | undefined): boolean {
    return this.getStatusClass(statusCode) === 'schedule-submissions-status-badge-waiting-submit';
  }

  isWaitingEducationHeadReviewStatus(statusCode: string | null | undefined): boolean {
    return this.getStatusClass(statusCode) === 'schedule-submissions-status-badge-waiting-review';
  }

  private initForm(): void {
    const defaultAcademicTerm = this.getDefaultAcademicTermName();
    const defaultYear = this.getDefaultBuddhistYear();

    this.academicTermOptions = [defaultAcademicTerm];
    this.yearOptions = [defaultYear];
    this.form = this.fb.group({
      academicTerm: [defaultAcademicTerm],
      year: [defaultYear],
      keyword: [''],
      departmentId: [null],
    });
  }

  private listenFilters(): void {
    this.filterSubscription = this.form.valueChanges
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
      )
      .subscribe(() => this.loadSubmissions(1));
  }

  private loadFilterOptions(): void {
    this.filterOptionsSubscription?.unsubscribe();
    this.filterOptionsSubscription = forkJoin({
      academicTerms: this.studyPeriodService.getAcademicTermYears().pipe(
        take(1),
        catchError((err) => {
          console.error(err);
          return of([] as AcademicTermYearModel[]);
        }),
      ),
      departments: this.canViewManagerFilters()
        ? this.userManagementService.getDepartments(false).pipe(
          take(1),
          catchError((err) => {
            console.error(err);
            return of([] as UserManagementOption[]);
          }),
        )
        : of([] as UserManagementOption[]),
    }).subscribe(({ academicTerms, departments }) => {
      this.academicTerms = academicTerms;
      const academicTermOptions = this.buildAcademicTermOptions(academicTerms);
      const yearOptions = this.buildAcademicYearOptions(academicTerms);

      this.academicTermOptions = academicTermOptions.length > 0 ? academicTermOptions : this.academicTermOptions;
      this.yearOptions = yearOptions.length > 0 ? yearOptions : this.yearOptions;
      this.departmentOptions = departments;
      this.ensureAcademicTermFilterValues();
      this.loadSubmissions(1);
    });
  }

  private initColumns(): void {
    const columns: any[] = [
      {
        prop: 'index',
        name: 'ลำดับ',
        sortable: true,
        cellTemplate: this.rowIndexTemplate,
        width: 72,
        minWidth: 72,
        maxWidth: 72,
      },
      {
        prop: 'academicTerm',
        name: 'เทอมการศึกษา',
        sortable: true,
        width: 120,
        minWidth: 120,
        maxWidth: 120,
      },
      {
        prop: 'year',
        name: 'ปีการศึกษา',
        sortable: true,
        width: 100,
        minWidth: 100,
        maxWidth: 110,
      },
      {
        prop: 'teacherName',
        name: 'ชื่อ - นามสกุล',
        sortable: true,
        width: 260,
        minWidth: 260,
      },
      {
        prop: 'departmentName',
        name: 'สาขาวิชา',
        sortable: true,
        width: 220,
        minWidth: 220,
      },
      {
        prop: 'statusLabel',
        name: 'สถานะ',
        sortable: false,
        cellTemplate: this.statusTemplate,
        cellClass: 'schedule-submissions-center-cell schedule-submissions-status-cell',
        headerClass: 'schedule-submissions-center-header schedule-submissions-status-header',
        width: 150,
        minWidth: 150,
      },
      {
        prop: 'updatedAt',
        name: 'วันที่อัปเดตสถานะ',
        sortable: true,
        width: 210,
        minWidth: 210,
      },
      {
        prop: 'action',
        name: 'จัดการ',
        sortable: false,
        cellTemplate: this.actionTemplate,
        width: 90,
        minWidth: 90,
        maxWidth: 90,
        cellClass: 'schedule-submissions-center-cell schedule-submissions-action-cell',
        headerClass: 'schedule-submissions-center-header schedule-submissions-action-header',
      },
    ];

    if (this.canShowBulkEducationHeadConfirm()) {
      columns.unshift({
        prop: 'bulkSelect',
        name: '',
        sortable: false,
        headerTemplate: this.bulkSelectHeaderTemplate,
        cellTemplate: this.bulkSelectCellTemplate,
        width: 52,
        minWidth: 52,
        maxWidth: 52,
        cellClass: 'schedule-submissions-center-cell schedule-submissions-bulk-select-cell',
        headerClass: 'schedule-submissions-center-header schedule-submissions-bulk-select-header',
      });
    }

    this.columns = columns;
  }

  private loadSubmissions(page = this.currentPage): void {
    this.currentPage = page;
    this.tableLoading.set(true);

    const { academicTerm, year, keyword, departmentId } = this.form.getRawValue();
    const selectedTerm = this.findAcademicTerm(academicTerm, year);
    const canUseManagerFilters = this.canViewManagerFilters();
    const searchKeyword = canUseManagerFilters ? String(keyword ?? '').trim() : '';

    this.loadSubscription?.unsubscribe();
    this.loadSubscription = this.scheduleSubmissionsService.getScheduleSubmissionsList({
      academic_term_id: selectedTerm?.id ?? null,
      keywords: searchKeyword !== '' ? searchKeyword : null,
      department_id: canUseManagerFilters ? departmentId ?? null : null,
      approval_review: this.isApprovalReviewMode(),
      only_approved: !this.isApprovalReviewMode(),
      sort: this.currentSortParams.sort,
      direction: this.currentSortParams.direction,
      page,
      limit: this.currentLimit,
    }).pipe(
      finalize(() => this.tableLoading.set(false)),
    ).subscribe({
      next: (response) => {
        const rows = response.data
          .map((item) => this.mapSubmissionToRow(item))
          .filter((row) => this.isApprovalReviewMode() || row.statusLabel === 'รับรองแล้ว');
        this.selectedSubmissionIds.set(new Set<number>());

        this.datasource.set(this.createDataSource(
          rows,
          response.page,
          response.limit,
          response.total,
          response.total_pages,
          (response.page - 1) * response.limit,
        ));
      },
      error: (err) => {
        console.error(err);
        this.datasource.set(this.createDataSource([], page, this.currentLimit));
      },
    });
  }

  private currentPageEligibleBulkRows(): ScheduleSubmissionRow[] {
    return this.datasource().data.filter((row) => this.canSelectForBulkEducationHeadConfirm(row));
  }

  private createDataSource(
    rows: ScheduleSubmissionRow[],
    page = 1,
    limit = 10,
    total = rows.length,
    totalPage = Math.max(Math.ceil(total / limit), 1),
    offset = (page - 1) * limit,
  ): ScheduleSubmissionsDataSource {
    const normalizedPage = Math.min(Math.max(page, 1), totalPage);

    return {
      data: rows,
      limit,
      offset: normalizedPage === page ? offset : (normalizedPage - 1) * limit,
      page: normalizedPage,
      total,
      total_page: totalPage,
    };
  }

  private findAcademicTerm(
    academicTermName: string,
    academicYear: string,
  ): AcademicTermYearModel | undefined {
    return this.academicTerms.find((term) => (
      String(term.name) === String(academicTermName) &&
      String(this.toBuddhistYear(term.year)) === String(academicYear)
    ));
  }

  private mapSubmissionToRow(item: ScheduleSubmissionsModel): ScheduleSubmissionRow {
    const statusDisplay = this.getListStatusDisplay(item.approval_status?.status_code ?? item.status);

    return {
      id: item.id,
      sequence: item.sequence,
      academicTerm: item.academic_term.name,
      year: String(this.toBuddhistYear(item.academic_term.year)),
      teacherName: this.formatTeacherName(item),
      departmentName: this.getDepartmentName(item),
      statusCode: statusDisplay.code,
      statusLabel: statusDisplay.label,
      updatedAt: this.formatDateTime(item.updated_at),
    };
  }

  private getListStatusDisplay(status: string | null | undefined): ScheduleSubmissionListStatusDisplay {
    return getScheduleSubmissionListStatusDisplay(status);
  }

  private formatTeacherName(item: ScheduleSubmissionsModel): string {
    const fullName = item.teacher.full_name.trim();
    const academicRank = String(item.teacher.academic_rank ?? '').trim();

    if (fullName === '') {
      return '-';
    }

    return academicRank === '' ? fullName : `${academicRank} ${fullName}`;
  }

  private getDepartmentName(item: ScheduleSubmissionsModel): string {
    const department = item.teacher.department;

    return department.name_th ||
      department.name ||
      department.name_en ||
      '-';
  }

  private formatDateTime(value?: string | null): string {
    if (value == null || value === '') {
      return '-';
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = this.toBuddhistYear(date.getFullYear());
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  }

  private toBuddhistYear(year: number): number {
    return year > 0 && year < 2400 ? year + 543 : year;
  }

  private buildAcademicTermOptions(terms: AcademicTermYearModel[]): string[] {
    return Array.from(new Set(
      terms
        .map((term) => String(term.name).trim())
        .filter(Boolean),
    )).sort((a, b) => a.localeCompare(b, 'th'));
  }

  private buildAcademicYearOptions(terms: AcademicTermYearModel[]): string[] {
    return Array.from(new Set(
      terms
        .map((term) => this.toBuddhistYear(term.year))
        .filter((year) => Number.isFinite(year))
        .map((year) => String(year)),
    )).sort((a, b) => Number(b) - Number(a));
  }

  private ensureAcademicTermFilterValues(): void {
    const currentAcademicTerm = String(this.form.get('academicTerm')?.value ?? '');
    const currentYear = String(this.form.get('year')?.value ?? '');

    if (this.findAcademicTerm(currentAcademicTerm, currentYear) != null || this.academicTerms.length === 0) {
      return;
    }

    const fallbackTerm = this.academicTerms[0];

    this.form.patchValue({
      academicTerm: String(fallbackTerm.name),
      year: String(this.toBuddhistYear(fallbackTerm.year)),
    }, { emitEvent: false });
  }

  private getDefaultAcademicTermName(): string {
    const sessionAcademicTermName = String(this.authService.currentUser()?.academic_term?.name ?? '').trim();

    return sessionAcademicTermName === '' ? '1' : sessionAcademicTermName;
  }

  private getDefaultBuddhistYear(): string {
    const sessionYear = Number(this.authService.currentUser()?.academic_term?.year);

    if (!Number.isFinite(sessionYear) || sessionYear <= 0) {
      return String(this.toBuddhistYear(new Date().getFullYear()));
    }

    return String(this.toBuddhistYear(sessionYear));
  }
}
